import { action, observable, runInAction, computed } from "mobx";
import { getChatAccess, getTulumStreams } from '../services/TulumRealityShowServices';

const DEFAULT_INTERVAL = 5000;

class TulumRealityShowStore {
  featuredStreamIntervalId = null;

  @observable streams = null;

  @observable selectedStreamID = null;
  @observable chatAccess = null;
  @observable loadingStream = false;
  @observable hasStreamError = false;
  @observable allowStreamChange = false;
  @observable isFollowingFeaturedStream = false;
  @observable shouldExcludeFeatured = false;

  @computed get selectedStream () {
    const selectedStream = this.streams?.find(stream => stream.id === this.selectedStreamID);
    return selectedStream;
  }

  @action selectStream (streamId) {
    if (!this.loadingStream || this.hasStreamError || this.allowStreamChange) {
      const selectedStream = this.streams?.find(stream => stream.id === streamId);
      this.selectedStreamID = selectedStream.id;
      this.toggleStreamLoading(true);
      
      if (this.hasStreamError) {
        this.toggleStreamError(false);
      }

      if (this.allowStreamChange) {
        this.toggleAllowStreamChange(false);
      }
    }
  }

  @action async getTulumStreams () {
    const { error, data } = await getTulumStreams();

    runInAction(() => {
      if (!error) {
        if (this.streams) {
          // If streams exists, that means that we are doing polling to check for featured stream and excludeFeatured param
          const shouldExclude = data.data.excludeFeatured;
          this.shouldExcludeFeatured = shouldExclude;

          if (shouldExclude && this.isFollowingFeaturedStream) {
            this.toggleFollowFeaturedStream(false);
          }

          if (shouldExclude) {
            this.excludeFeaturedStream();
          } else {
            this.checkForFeaturedStream(data.data.content.users);
          }

          return;
        }

        let streams;

        if (data.data.excludeFeatured) {
          this.shouldExcludeFeatured = true;
          streams = data.data.content.users.filter(stream => !stream.tulumFeatured);
        } else {
          streams = data.data.content.users;
        }

        const formattedStreams = this.formatStreams(streams);
        const participantsProfiles = this.getParticipantsProfiles();

        if (formattedStreams.length > 0) {
          this.selectedStreamID = formattedStreams[0].id;
        }

        if (!this.chatAccess) {
          this.getChatAccess();
        }

        if (formattedStreams.length > 0) {
          this.streams = [...formattedStreams, ...participantsProfiles];
        }

        // start the polling to keep receiving updates about excludeFeatured (if true, we need to remove featured stream from the UI)
        if (!this.featuredStreamIntervalId) {
          this.startFeaturedStreamInterval();
        }
      }
    });
  }

  @action async getChatAccess () {
    const { error, data } = await getChatAccess();

    runInAction(() => {
      const { chatServiceUrl, roomName, token, username } = data.data;
      if (!error) {
        this.chatAccess = {
          chatServiceUrl,
          roomName,
          token,
          username,
        }
      }
    });
  }

  @action excludeFeaturedStream () {
    // check if user has featured stream in his camera preview
    const featuredStream = this.findFeaturedStreamFromCurrentList();
    
    // if user has the featured stream, remove it from his camera preview
    if (featuredStream) {
      const filteredStreams = this.streams.filter(stream => !stream.isFeatured);
      this.streams = filteredStreams;

      // if user is currently watching the featured stream, change the camera
      if (featuredStream.id === this.selectedStreamID) {
        this.selectedStreamID = filteredStreams[0].id;
      }
    }
  }

  @action checkForFeaturedStream (streams) {
    const featuredStreamFromResponse = streams.find(stream => stream.tulumFeatured);
    const featuredStreamFromList = this.findFeaturedStreamFromCurrentList();

    // Check if featured stream was previously removed from the UI, if yes and excludeFeatured is false, we need to put it back
    if (!featuredStreamFromList && featuredStreamFromResponse) {
      const formattedStreams = this.formatStreams(streams);
      const participantsProfiles = this.getParticipantsProfiles();

      if (formattedStreams.length > 0) {
        this.selectedStreamID = formattedStreams[0].id;
        this.streams = [...formattedStreams, ...participantsProfiles];
      }
    }
    
    // Check for featured stream in response only if user has chosen to follow it
    if (this.isFollowingFeaturedStream) {
      if (featuredStreamFromResponse && featuredStreamFromResponse.userId !== this.selectedStreamID) {
        this.selectedStreamID = featuredStreamFromResponse.userId;
      }
    }
  }

  @action toggleFollowFeaturedStream (isFollowing) {
    this.isFollowingFeaturedStream = isFollowing;
  }

  @action toggleStreamLoading (isLoading) {
    this.loadingStream = isLoading;
  }

  @action toggleStreamError (hasError) {
    this.hasStreamError = hasError;
  }

  @action toggleAllowStreamChange (canChange) {
    this.allowStreamChange = canChange;
  }

  findFeaturedStreamFromCurrentList () {
    return this.streams?.find(stream => stream.isFeatured);
  }

  startFeaturedStreamInterval () {
    this.featuredStreamIntervalId = setInterval(() => {
      this.getTulumStreams();
    }, DEFAULT_INTERVAL);
  }

  stopFeaturedStreamInterval () {
    if (this.featuredStreamIntervalId) {
      clearInterval(this.featuredStreamIntervalId);
      this.featuredStreamIntervalId = null;
    }
  }

  formatStreams (streams) {
    const formattedStreams = streams?.filter(stream => stream.tulumShow === true).map(stream => ({
      id: stream.userId,
      provider: 'WOWZA',
      streamingSourceType: 'TEMPTED',
      streamViews: [{
        url: stream.hlsPreviewUrl,
      }],
      imageUrl: stream.snapshotImageLink,
      isFeatured: stream.tulumFeatured,
    }));

    return formattedStreams;
  }

  getParticipantsProfiles () {
    return [
      {
        id: 1,
        name: 'Drew',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/tulum/tulum_participants/Drew.png',
        profileUrl: 'https://tempted.com/DrewCatherine'
      },
      {
        id: 2,
        name: 'Kitty',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/tulum/tulum_participants/Kitty.png',
        profileUrl: 'https://tempted.com/KittyQuinn'
      },
      {
        id: 3,
        name: 'Eila',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/tulum/tulum_participants/Eila.png',
        profileUrl: 'https://www.nakednews.com/naked-news-anchor-eila-adams-a64?filter=fanzone'
      },
      {
        id: 4,
        name: 'Marina',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/tulum/tulum_participants/Marina.png',
        profileUrl: 'https://www.nakednews.com/naked-news-anchor-marina-valmont-a71?filter=fanzone'
      },
      {
        id: 5,
        name: 'Whitney',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/tulum/tulum_participants/Whitney.png',
        profileUrl: 'https://www.nakednews.com/naked-news-anchor-whitney-st.-john-a58?filter=fanzone'
      },
      {
        id: 6,
        name: 'Cecelia',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/tulum/tulum_participants/Cecelia.png',
        profileUrl: 'https://www.nakednews.com/naked-news-anchor-cecelia-sommer-a244?filter=fanzone'
      },
      {
        id: 7,
        name: 'Vee',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/tulum/tulum_participants/Vee.png',
        profileUrl: 'https://tempted.com/VeeJeen'
      },
      {
        id: 8,
        name: 'N&U',
        profileImage: 'https://tempted-prod.xcdnpro.com/data/anchors/images/000/000/030/641/1551080003-478287_xl_thumbnail.jpg',
        profileUrl: 'http://tempted.com/nakedanduncut'
      }
    ];
  }

  @action reset () {
    this.chatAccess = null;
    this.selectedStreamID = null;
    this.hasStreamError = false;
    this.allowStreamChange = false;
    this.streams = null;
    this.isFollowingFeaturedStream = false;
    this.shouldExcludeFeatured = false;
    this.stopFeaturedStreamInterval();
  }
}

export default new TulumRealityShowStore();